import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Users,
  Search,
  Mail,
  Phone,
  Globe,
  Link2,
  Unlink,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/common/Card';
import { Badge } from '@/components/common/Badge';
import { Button } from '@/components/common/Button';
import { Input } from '@/components/common/Input';
import { Modal } from '@/components/common/Modal';
import { SlideOverPanel } from '@/components/common/SlideOverPanel';
import { Spinner } from '@/components/common/Spinner';
import { cn } from '@/utils/cn';
import toast from 'react-hot-toast';
import {
  contactService,
  type Contact,
  type ContactDetail,
  type PotentialMatch,
  type TimelineMessage,
} from '@/services/contact.service';
import { DocumentsTab } from './DocumentsTab';

type TabType = 'contacts' | 'matches';

const PAGE_SIZE = 20;

const channelColors: Record<string, string> = {
  telegram: 'bg-blue-100 text-blue-700',
  whatsapp: 'bg-green-100 text-green-700',
  messenger: 'bg-purple-100 text-purple-700',
  instagram: 'bg-pink-100 text-pink-700',
  web_widget: 'bg-gray-100 text-gray-700',
};

export const ContactsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('contacts');

  // Contacts list
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [contactsTotal, setContactsTotal] = useState(0);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contactsPage, setContactsPage] = useState(0);
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [scoreFilter, setScoreFilter] = useState<string>('all');
  const [scoreSortDesc, setScoreSortDesc] = useState<boolean | null>(null);

  // Contact detail
  const [selectedContact, setSelectedContact] = useState<ContactDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Potential matches
  const [matches, setMatches] = useState<PotentialMatch[]>([]);
  const [matchesTotal, setMatchesTotal] = useState(0);
  const [matchesLoading, setMatchesLoading] = useState(false);
  const [matchesPage, setMatchesPage] = useState(0);

  // Link/Unlink modal
  const [linkModal, setLinkModal] = useState<{
    type: 'link' | 'unlink';
    contactId: string;
    customerId: string;
    customerName?: string;
  } | null>(null);
  const [linkReason, setLinkReason] = useState('');
  const [linkLoading, setLinkLoading] = useState(false);

  // Timeline
  const [timeline, setTimeline] = useState<TimelineMessage[]>([]);
  const [timelineTotal, setTimelineTotal] = useState(0);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [detailTab, setDetailTab] = useState<'info' | 'timeline' | 'documents'>('info');

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setSearchDebounced(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Fetch contacts
  const fetchContacts = useCallback(async () => {
    setContactsLoading(true);
    try {
      const result = await contactService.list({
        search: searchDebounced || undefined,
        limit: PAGE_SIZE,
        offset: contactsPage * PAGE_SIZE,
      });
      setContacts(result.contacts);
      setContactsTotal(result.total);
    } catch {
      toast.error('Failed to load contacts');
    } finally {
      setContactsLoading(false);
    }
  }, [searchDebounced, contactsPage]);

  // Fetch matches
  const fetchMatches = useCallback(async () => {
    setMatchesLoading(true);
    try {
      const result = await contactService.getPotentialMatches({
        limit: PAGE_SIZE,
        offset: matchesPage * PAGE_SIZE,
      });
      setMatches(result.matches);
      setMatchesTotal(result.total);
    } catch {
      toast.error('Failed to load potential matches');
    } finally {
      setMatchesLoading(false);
    }
  }, [matchesPage]);

  useEffect(() => {
    if (activeTab === 'contacts') {
      fetchContacts();
    }
  }, [activeTab, fetchContacts]);

  useEffect(() => {
    if (activeTab === 'matches') {
      fetchMatches();
    }
  }, [activeTab, fetchMatches]);

  // Reset page when search changes
  useEffect(() => {
    setContactsPage(0);
  }, [searchDebounced]);

  // Load contact detail
  const openContactDetail = async (contactId: string) => {
    setDetailLoading(true);
    try {
      const detail = await contactService.getById(contactId);
      setSelectedContact(detail);
    } catch {
      toast.error('Failed to load contact details');
    } finally {
      setDetailLoading(false);
    }
  };

  // Link/Unlink actions
  const handleLinkAction = async () => {
    if (!linkModal || !linkReason.trim()) {
      toast.error('Reason is required');
      return;
    }
    setLinkLoading(true);
    try {
      if (linkModal.type === 'link') {
        await contactService.linkCustomer(linkModal.contactId, linkModal.customerId, linkReason);
        toast.success('Customer linked to contact');
      } else {
        await contactService.unlinkCustomer(linkModal.contactId, linkModal.customerId, linkReason);
        toast.success('Customer unlinked from contact');
      }
      setLinkModal(null);
      setLinkReason('');
      // Refresh data
      if (selectedContact) {
        openContactDetail(selectedContact.contact.id);
      }
      fetchContacts();
      if (activeTab === 'matches') fetchMatches();
    } catch {
      toast.error(`Failed to ${linkModal.type} customer`);
    } finally {
      setLinkLoading(false);
    }
  };

  // Load timeline
  const loadTimeline = async (contactId: string) => {
    setTimelineLoading(true);
    try {
      const result = await contactService.getTimeline(contactId, { limit: 100 });
      setTimeline(result.messages);
      setTimelineTotal(result.total);
    } catch {
      toast.error('Failed to load timeline');
    } finally {
      setTimelineLoading(false);
    }
  };

  useEffect(() => {
    if (detailTab === 'timeline' && selectedContact) {
      loadTimeline(selectedContact.contact.id);
    }
  }, [detailTab, selectedContact?.contact.id]);

  const displayContacts = useMemo(() => {
    let filtered = contacts;
    if (scoreFilter !== 'all') {
      filtered = filtered.filter(c => {
        const score = (c.metadata as any)?.lead_score;
        if (scoreFilter === 'unscored') return !score;
        if (scoreFilter === 'hot') return score >= 9;
        if (scoreFilter === 'good') return score >= 7 && score <= 8;
        if (scoreFilter === 'medium') return score >= 5 && score <= 6;
        if (scoreFilter === 'low') return score >= 3 && score <= 4;
        if (scoreFilter === 'minimal') return score >= 1 && score <= 2;
        return true;
      });
    }
    if (scoreSortDesc !== null) {
      filtered = [...filtered].sort((a, b) => {
        const sa = (a.metadata as any)?.lead_score || 0;
        const sb = (b.metadata as any)?.lead_score || 0;
        return scoreSortDesc ? sb - sa : sa - sb;
      });
    }
    return filtered;
  }, [contacts, scoreFilter, scoreSortDesc]);

  const totalPages = Math.ceil(contactsTotal / PAGE_SIZE);
  const matchesTotalPages = Math.ceil(matchesTotal / PAGE_SIZE);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold">Contacts</h1>
        <p className="text-muted-foreground mt-1">Unified customer identities across channels</p>
      </div>

      {/* Tabs */}
      <div className="border-b-2 border-border">
        <nav className="flex gap-4">
          <button
            onClick={() => setActiveTab('contacts')}
            className={cn(
              'flex items-center gap-2 px-4 py-3 border-b-2 transition-colors',
              activeTab === 'contacts'
                ? 'border-primary text-primary font-medium'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            )}
          >
            <Users className="h-4 w-4" />
            Contacts
            {contactsTotal > 0 && (
              <span className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded-full">{contactsTotal}</span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('matches')}
            className={cn(
              'flex items-center gap-2 px-4 py-3 border-b-2 transition-colors',
              activeTab === 'matches'
                ? 'border-primary text-primary font-medium'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            )}
          >
            <AlertTriangle className="h-4 w-4" />
            Potential Matches
            {matchesTotal > 0 && (
              <span className="text-xs bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded-full">{matchesTotal}</span>
            )}
          </button>
        </nav>
      </div>

      {/* =========== Contacts Tab =========== */}
      {activeTab === 'contacts' && (
        <div className="space-y-4">
          {/* Search + Score Filter */}
          <div className="flex items-center gap-3">
            <div className="relative max-w-md flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search by name, email, or phone..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 text-sm border-2 border-[#1a1a2e] rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              />
            </div>
            <select
              value={scoreFilter}
              onChange={e => setScoreFilter(e.target.value)}
              className="px-3 py-2 text-xs border-2 border-gray-200 rounded-lg focus:border-blue-300 focus:outline-none bg-white"
            >
              <option value="all">All Scores</option>
              <option value="hot">{'\uD83D\uDFE2'} Hot (9-10)</option>
              <option value="good">{'\uD83D\uDD35'} Good (7-8)</option>
              <option value="medium">{'\uD83D\uDFE1'} Medium (5-6)</option>
              <option value="low">{'\uD83D\uDFE0'} Low (3-4)</option>
              <option value="minimal">{'\u26AA'} Minimal (1-2)</option>
              <option value="unscored">&mdash; Unscored</option>
            </select>
          </div>

          {/* Contacts Table */}
          <Card>
            <CardContent className="p-0">
              {contactsLoading ? (
                <div className="flex items-center justify-center py-16">
                  <Spinner size="lg" />
                </div>
              ) : displayContacts.length === 0 ? (
                <div className="text-center py-16">
                  <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">
                    {searchDebounced || scoreFilter !== 'all' ? 'No contacts match your filters' : 'No contacts yet'}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Contacts are created automatically when customers provide email or phone
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-gray-50/50">
                        <th className="text-left font-medium text-muted-foreground px-4 py-3">Name</th>
                        <th
                          className="text-center font-medium text-muted-foreground px-4 py-3 cursor-pointer hover:text-foreground select-none"
                          onClick={() => setScoreSortDesc(prev => prev === null ? true : prev ? false : null)}
                        >
                          Score {scoreSortDesc === true ? '\u2193' : scoreSortDesc === false ? '\u2191' : ''}
                        </th>
                        <th className="text-left font-medium text-muted-foreground px-4 py-3">Email</th>
                        <th className="text-left font-medium text-muted-foreground px-4 py-3">Phone</th>
                        <th className="text-left font-medium text-muted-foreground px-4 py-3">Channels</th>
                        <th className="text-center font-medium text-muted-foreground px-4 py-3">Conversations</th>
                        <th className="text-left font-medium text-muted-foreground px-4 py-3">Last Updated</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {displayContacts.map((contact) => (
                        <tr
                          key={contact.id}
                          onClick={() => openContactDetail(contact.id)}
                          className="hover:bg-gray-50 cursor-pointer transition-colors"
                        >
                          <td className="px-4 py-3 font-medium text-gray-900">
                            {contact.name && contact.name !== 'Unnamed' ? (
                              contact.name
                            ) : (
                              <span className="text-gray-400 italic">
                                {contact.phone || contact.email || 'Unknown Contact'}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-center">
                            {(contact.metadata as any)?.lead_score ? (
                              <span className={cn(
                                'inline-flex items-center justify-center w-7 h-7 rounded-lg text-xs font-black text-white',
                                (contact.metadata as any).lead_score >= 9 ? 'bg-emerald-500' :
                                (contact.metadata as any).lead_score >= 7 ? 'bg-blue-500' :
                                (contact.metadata as any).lead_score >= 5 ? 'bg-amber-400' :
                                (contact.metadata as any).lead_score >= 3 ? 'bg-orange-400' : 'bg-gray-400'
                              )}>
                                {(contact.metadata as any).lead_score}
                              </span>
                            ) : (
                              <span className="text-gray-300 text-xs">&mdash;</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-muted-foreground">
                            {contact.email || '-'}
                          </td>
                          <td className="px-4 py-3 text-muted-foreground">
                            {contact.phone || '-'}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap gap-1">
                              {(contact.linked_channels || []).filter(Boolean).map((ch) => (
                                <Badge
                                  key={ch}
                                  variant="outline"
                                  className={cn('text-xs capitalize', channelColors[ch] || 'bg-gray-100 text-gray-700')}
                                >
                                  {ch.replace('_', ' ')}
                                </Badge>
                              ))}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center text-muted-foreground">
                            {contact.conversations_count || 0}
                          </td>
                          <td className="px-4 py-3 text-muted-foreground text-xs">
                            {new Date(contact.updated_at).toLocaleDateString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t">
                  <span className="text-sm text-muted-foreground">
                    {contactsTotal} contact{contactsTotal !== 1 ? 's' : ''}
                  </span>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={contactsPage === 0}
                      onClick={() => setContactsPage(p => p - 1)}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm text-muted-foreground">
                      {contactsPage + 1} / {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={contactsPage >= totalPages - 1}
                      onClick={() => setContactsPage(p => p + 1)}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* =========== Potential Matches Tab =========== */}
      {activeTab === 'matches' && (
        <Card>
          <CardHeader>
            <CardTitle className="text-gray-900">Unlinked Customers with Matching Identifiers</CardTitle>
          </CardHeader>
          <CardContent>
            {matchesLoading ? (
              <div className="flex items-center justify-center py-16">
                <Spinner size="lg" />
              </div>
            ) : matches.length === 0 ? (
              <div className="text-center py-16">
                <Link2 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No potential matches found</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Matches appear when customers share email or phone with existing contacts
                </p>
              </div>
            ) : (
              <div className="divide-y">
                {matches.map((match, idx) => (
                  <div key={idx} className="flex items-center justify-between py-4 first:pt-0 last:pb-0">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-sm text-gray-900">
                          {match.customer.name || match.customer.external_id}
                        </p>
                        <Badge variant="outline" className={cn('text-xs capitalize', channelColors[match.customer.channel_type || ''] || '')}>
                          {match.customer.channel_type?.replace('_', ' ')}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        Matches contact <span className="font-medium">{match.contact.name || match.contact.id.slice(0, 8)}</span> on{' '}
                        <Badge variant="info" className="text-xs">{match.matched_on}</Badge>
                      </p>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => setLinkModal({
                        type: 'link',
                        contactId: match.contact.id,
                        customerId: match.customer.id,
                        customerName: match.customer.name || match.customer.external_id,
                      })}
                    >
                      <Link2 className="h-3.5 w-3.5 mr-1.5" />
                      Link
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {/* Pagination */}
            {matchesTotalPages > 1 && (
              <div className="flex items-center justify-between pt-4 border-t mt-4">
                <span className="text-sm text-muted-foreground">{matchesTotal} match{matchesTotal !== 1 ? 'es' : ''}</span>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" disabled={matchesPage === 0} onClick={() => setMatchesPage(p => p - 1)}>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-sm text-muted-foreground">{matchesPage + 1} / {matchesTotalPages}</span>
                  <Button variant="outline" size="sm" disabled={matchesPage >= matchesTotalPages - 1} onClick={() => setMatchesPage(p => p + 1)}>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* =========== Contact Detail Panel =========== */}
      <SlideOverPanel
        isOpen={!!selectedContact}
        onClose={() => { setSelectedContact(null); setDetailTab('info'); }}
        title={selectedContact?.contact.name || 'Contact Details'}
        subtitle={selectedContact?.contact.email || selectedContact?.contact.phone || undefined}
        size="lg"
      >
        {detailLoading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner size="lg" />
          </div>
        ) : selectedContact ? (
          <div className="space-y-4">
            {/* Tab Switcher */}
            <div className="border-b-2 border-border mb-4">
              <nav className="flex gap-4">
                <button
                  onClick={() => setDetailTab('info')}
                  className={cn(
                    'px-3 py-2 text-sm border-b-2 transition-colors',
                    detailTab === 'info'
                      ? 'border-primary text-primary font-medium'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  )}
                >
                  Info
                </button>
                <button
                  onClick={() => setDetailTab('timeline')}
                  className={cn(
                    'px-3 py-2 text-sm border-b-2 transition-colors',
                    detailTab === 'timeline'
                      ? 'border-primary text-primary font-medium'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  )}
                >
                  Timeline ({timelineTotal})
                </button>
                <button
                  onClick={() => setDetailTab('documents')}
                  className={cn(
                    'px-3 py-2 text-sm border-b-2 transition-colors',
                    detailTab === 'documents'
                      ? 'border-primary text-primary font-medium'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  )}
                >
                  Documents
                </button>
              </nav>
            </div>

            {detailTab === 'documents' && selectedContact ? (
              <DocumentsTab contactId={selectedContact.contact.id} />
            ) : detailTab === 'info' ? (
              <div className="space-y-6">
                {/* Contact Info */}
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">Info</h3>
                  <div className="grid grid-cols-2 gap-3">
                    {selectedContact.contact.email && (
                      <div className="flex items-center gap-2 text-sm">
                        <Mail className="h-4 w-4 text-muted-foreground" />
                        <span>{selectedContact.contact.email}</span>
                      </div>
                    )}
                    {selectedContact.contact.phone && (
                      <div className="flex items-center gap-2 text-sm">
                        <Phone className="h-4 w-4 text-muted-foreground" />
                        <span>{selectedContact.contact.phone}</span>
                      </div>
                    )}
                    {selectedContact.contact.preferred_language && (
                      <div className="flex items-center gap-2 text-sm">
                        <Globe className="h-4 w-4 text-muted-foreground" />
                        <span>{selectedContact.contact.preferred_language}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Unified Context */}
                {selectedContact.unified_context && (
                  <div className="space-y-3">
                    <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">Activity</h3>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-gray-50 rounded-lg p-3">
                        <p className="text-xs text-muted-foreground">Total Conversations</p>
                        <p className="text-lg font-semibold">{selectedContact.unified_context.conversation_count}</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-3">
                        <p className="text-xs text-muted-foreground">Active Channels</p>
                        <p className="text-lg font-semibold">{selectedContact.unified_context.channels.length}</p>
                      </div>
                    </div>
                    {selectedContact.unified_context.last_interaction && (
                      <div className="bg-gray-50 rounded-lg p-3">
                        <p className="text-xs text-muted-foreground">Last Interaction</p>
                        <p className="text-sm mt-1">
                          <span className="capitalize">{selectedContact.unified_context.last_interaction.channel.replace('_', ' ')}</span>
                          {' - '}
                          {new Date(selectedContact.unified_context.last_interaction.date).toLocaleDateString()}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Linked Customers */}
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">
                    Linked Channels ({selectedContact.linked_customers.length})
                  </h3>
                  {selectedContact.linked_customers.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No linked customers</p>
                  ) : (
                    <div className="space-y-2">
                      {selectedContact.linked_customers.map((customer) => (
                        <div
                          key={customer.id}
                          className="flex items-center justify-between p-3 rounded-lg border-2 border-[#1a1a2e]"
                        >
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-sm text-gray-900 truncate">
                                {customer.name || customer.external_id}
                              </span>
                              <Badge
                                variant="outline"
                                className={cn('text-xs capitalize', channelColors[customer.channel_type || ''] || '')}
                              >
                                {customer.channel_type?.replace('_', ' ')}
                              </Badge>
                              {customer.verified && (
                                <Badge variant="success" className="text-xs">Verified</Badge>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground mt-0.5">
                              Linked by: {customer.linked_by}
                            </p>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setLinkModal({
                              type: 'unlink',
                              contactId: selectedContact.contact.id,
                              customerId: customer.id,
                              customerName: customer.name || customer.external_id,
                            })}
                          >
                            <Unlink className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Collected Data */}
                {selectedContact.unified_context?.collected_data &&
                  Object.keys(selectedContact.unified_context.collected_data).length > 0 && (
                  <div className="space-y-3">
                    <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">Collected Data</h3>
                    <div className="bg-gray-50 rounded-lg p-3 space-y-1">
                      {Object.entries(selectedContact.unified_context.collected_data).map(([key, value]) => (
                        <div key={key} className="flex justify-between text-sm">
                          <span className="text-muted-foreground capitalize">{key.replace(/_/g, ' ')}</span>
                          <span className="font-medium text-gray-900">{String(value)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-1">
                {timelineLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Spinner size="lg" />
                  </div>
                ) : timeline.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No messages yet</p>
                ) : (
                  timeline.map((msg, idx) => {
                    const isAgent = msg.sender_type === 'agent' || msg.sender_type === 'system';
                    const channelColor: Record<string, string> = {
                      facebook: 'bg-blue-500',
                      messenger: 'bg-blue-500',
                      telegram: 'bg-sky-500',
                      whatsapp: 'bg-green-500',
                      instagram: 'bg-pink-500',
                      web_widget: 'bg-gray-500',
                    };
                    const dotColor = channelColor[msg.channel_type] || 'bg-gray-400';

                    const prevMsg = idx > 0 ? timeline[idx - 1] : null;
                    const currDate = new Date(msg.created_at).toLocaleDateString();
                    const prevDate = prevMsg ? new Date(prevMsg.created_at).toLocaleDateString() : null;
                    const showDateSep = currDate !== prevDate;

                    return (
                      <React.Fragment key={msg.id}>
                        {showDateSep && (
                          <div className="flex items-center gap-2 py-2">
                            <div className="flex-1 h-px bg-border" />
                            <span className="text-xs text-muted-foreground">{currDate}</span>
                            <div className="flex-1 h-px bg-border" />
                          </div>
                        )}
                        <div className={cn('flex gap-2 py-1.5', isAgent ? 'justify-end' : 'justify-start')}>
                          <div className={cn(
                            'max-w-[80%] rounded-lg px-3 py-2 text-sm',
                            isAgent ? 'bg-primary/10 text-foreground' : 'bg-muted text-foreground'
                          )}>
                            <div className="flex items-center gap-1.5 mb-0.5">
                              <span className={cn('w-2 h-2 rounded-full', dotColor)} />
                              <span className="text-[10px] text-muted-foreground capitalize">
                                {msg.channel_type.replace('_', ' ')}
                              </span>
                              <span className="text-[10px] text-muted-foreground">
                                {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </span>
                            </div>
                            <p className="whitespace-pre-wrap break-words">{msg.content}</p>
                          </div>
                        </div>
                      </React.Fragment>
                    );
                  })
                )}
              </div>
            )}
          </div>
        ) : null}
      </SlideOverPanel>

      {/* =========== Link/Unlink Modal =========== */}
      <Modal isOpen={!!linkModal} onClose={() => { setLinkModal(null); setLinkReason(''); }}>
        <div>
          <h2 className="text-xl font-bold text-gray-900 mb-4">
            {linkModal?.type === 'link' ? 'Link Customer' : 'Unlink Customer'}
          </h2>
          <p className="text-sm text-muted-foreground mb-4">
            {linkModal?.type === 'link'
              ? `Link "${linkModal.customerName}" to this contact.`
              : `Unlink "${linkModal?.customerName}" from this contact.`}
          </p>
          <Input
            label="Reason"
            placeholder="Enter reason for this action..."
            value={linkReason}
            onChange={(e) => setLinkReason(e.target.value)}
            required
          />
          <div className="flex items-center gap-3 mt-6">
            <Button onClick={handleLinkAction} isLoading={linkLoading}>
              {linkModal?.type === 'link' ? (
                <>
                  <Link2 className="h-4 w-4 mr-2" />
                  Link
                </>
              ) : (
                <>
                  <Unlink className="h-4 w-4 mr-2" />
                  Unlink
                </>
              )}
            </Button>
            <Button variant="outline" onClick={() => { setLinkModal(null); setLinkReason(''); }}>
              Cancel
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
